import { useState, useRef, useCallback, useEffect } from "react";
import type { ApiClient } from "../api/client";
import { useMediaRecorder } from "./useMediaRecorder";
import type { MicPermission } from "./useMediaRecorder";
import { useSilenceDetection } from "./useSilenceDetection";

export type VoiceInputState =
  | "idle"
  | "recording"
  | "transcribing"
  | "error"
  | "permission-denied";

interface UseVoiceInputResult {
  voiceInputState: VoiceInputState;
  isVoiceSupported: boolean;
  micPermission: MicPermission;
  startRecording: (onPermissionDenied?: () => void) => void;
  stopAndSubmit: () => void;
  cancelRecording: () => void;
}

export function useVoiceInput(
  apiClient: ApiClient,
  onTranscript: (text: string, detectedLanguage?: string) => void,
  language?: string,
): UseVoiceInputResult {
  const [transcriptionState, setTranscriptionState] = useState<
    "idle" | "transcribing" | "error" | "permission-denied"
  >("idle");

  const onTranscriptRef = useRef(onTranscript);
  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  const languageRef = useRef(language);
  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  const {
    state: recorderState,
    stream,
    isSupported: isVoiceSupported,
    micPermission,
    startRecording,
    stopRecording,
    cancelRecording: cancelMediaRecording,
  } = useMediaRecorder();

  const wrappedStartRecording = useCallback(
    (onPermissionDenied?: () => void) => {
      startRecording(() => {
        setTranscriptionState("permission-denied");
        setTimeout(() => setTranscriptionState("idle"), 3000);
        onPermissionDenied?.();
      });
    },
    [startRecording],
  );

  const stopAndSubmit = useCallback(() => {
    stopRecording(async (blob, mimeType) => {
      setTranscriptionState("transcribing");
      try {
        const result = await apiClient.transcribeAudio(
          blob,
          mimeType,
          languageRef.current,
        );
        setTranscriptionState("idle");
        onTranscriptRef.current(result.transcript, result.language);
      } catch (error) {
        const isNoSpeech =
          error instanceof Error && error.message === "NO_SPEECH_DETECTED";
        setTranscriptionState("error");
        if (isNoSpeech) {
          onTranscriptRef.current("", undefined);
        }
        setTimeout(() => setTranscriptionState("idle"), 3000);
      }
    });
  }, [apiClient, stopRecording]);

  const cancelRecording = useCallback(() => {
    cancelMediaRecording();
    setTranscriptionState("idle");
  }, [cancelMediaRecording]);

  useSilenceDetection(stream, recorderState === "recording", stopAndSubmit);

  const voiceInputState: VoiceInputState =
    transcriptionState !== "idle"
      ? transcriptionState
      : recorderState === "recording"
        ? "recording"
        : "idle";

  return {
    voiceInputState,
    isVoiceSupported,
    micPermission,
    startRecording: wrappedStartRecording,
    stopAndSubmit,
    cancelRecording,
  };
}
